/* ────────────────────────────────────────────────────────────────────
 * JSON-LD structured data (schema.org).
 *
 * Emitted on the two homepages (Organization + WebSite) and the two About
 * pages (Organization + AboutPage). Every other route emits nothing:
 * organization-level facts belong on a few pages, not on all of them, and a
 * WebPage node per legal page would add no fact a crawler cannot already read
 * from the title, description and canonical.
 *
 * The Organization node is DUPLICATED on home and about rather than referenced
 * by @id from about, so that page resolves standalone for a crawler that never
 * fetches the homepage. Both come from organization() below, so the two copies
 * cannot drift.
 *
 * WHY THE @id IS PER-LOCALE. /en/ and /ar/ describe the same company in two
 * languages. Plain JSON-LD has no way to attach a language to a string that
 * Google reliably reads, so one shared @id would mean two contradictory
 * definitions of one node. Each locale therefore names its own node
 * (https://prinotes.com/en/#organization) and hreflang tells Google they are
 * translations. Same reasoning as the per-page canonical: one identifier must
 * describe exactly one thing.
 *
 * sameAs IS DELIBERATELY ABSENT. It is the field search engines use to
 * reconcile this entity with its profiles elsewhere, so a URL that does not
 * resolve to a real profile is worse than no field at all. The three Connect
 * links in the footer are not confirmed live (LinkedIn 404s to an anonymous
 * fetch as of 2026-09-03). Add sameAs once the owner confirms the profiles
 * exist, not before.
 *
 * The strings here restate facts authored on the About page. structuredData
 * .test.ts asserts they still match copy/about-en.ts and copy/about-ar.ts, so
 * editing the About page without editing this file fails the build.
 * ──────────────────────────────────────────────────────────────────── */
import { SITE_ORIGIN, urlFor, type RouteStem } from '@/config/routes';
import { metaFor } from './meta-en';
import { BRAND } from './socialCard';
import type { Locale } from '@/strings/types';

/* Company facts. The National Number is the establishment identifier shown on
 * /about under the Saudi E-Commerce Law disclosure (MoC Trader's Guide p.8);
 * it is a public register number, not a secret. */
export const ORG_NATIONAL_NUMBER = '7053899808';
export const ORG_EMAIL = 'info@prinotes.com';
export const ORG_LOGO_PATH = 'favicons/icon-512.png';

/* name is the brand people search for; legalName is the registered entity.
 * Splitting them is what lets a brand SERP read "Prinotes" while the markup
 * still carries the company that signs the Terms. */
const LEGAL_NAME: Record<Locale, string> = { en: 'Priyora Company', ar: 'شركة برييورا' };
const LOCALITY: Record<Locale, string> = { en: 'Jeddah', ar: 'جدة' };
const NATIONAL_NUMBER_LABEL: Record<Locale, string> = {
  en: 'Unified National Number',
  ar: 'الرقم الوطني الموحد',
};

/* contactType stays English in both locales: it is a machine enumeration
 * consumers match on, not display copy. The addresses mirror ABOUT_*.contacts. */
const CONTACTS = [
  { contactType: 'customer service', email: ORG_EMAIL },
  { contactType: 'customer support', email: 'support@prinotes.com' },
  { contactType: 'privacy', email: 'privacy@prinotes.com' },
] as const;

const orgId = (locale: Locale) => `${urlFor(locale, '')}#organization`;
const siteId = (locale: Locale) => `${urlFor(locale, '')}#website`;
const logoUrl = `${SITE_ORIGIN}/${ORG_LOGO_PATH}`;

export interface JsonLdGraph {
  '@context': 'https://schema.org';
  '@graph': Record<string, unknown>[];
}

function organization(locale: Locale): Record<string, unknown> {
  return {
    '@type': 'Organization',
    '@id': orgId(locale),
    name: BRAND[locale],
    legalName: LEGAL_NAME[locale],
    url: urlFor(locale, ''),
    /* A raster logo, not the SVG lockup: Google's logo guidance wants a square
     * image of at least 112x112, and icon-512.png is already served. */
    logo: { '@type': 'ImageObject', url: logoUrl, width: 512, height: 512 },
    image: logoUrl,
    description: metaFor(locale).pages[''].description,
    email: ORG_EMAIL,
    identifier: {
      '@type': 'PropertyValue',
      name: NATIONAL_NUMBER_LABEL[locale],
      value: ORG_NATIONAL_NUMBER,
    },
    address: {
      '@type': 'PostalAddress',
      addressLocality: LOCALITY[locale],
      addressCountry: 'SA',
    },
    knowsLanguage: ['ar', 'en'],
    contactPoint: CONTACTS.map((c) => ({
      '@type': 'ContactPoint',
      contactType: c.contactType,
      email: c.email,
      availableLanguage: ['ar', 'en'],
    })),
  };
}

/* No potentialAction/SearchAction: the site has no search endpoint, and a
 * declared action that 404s is a claim the site cannot honour. */
function website(locale: Locale): Record<string, unknown> {
  return {
    '@type': 'WebSite',
    '@id': siteId(locale),
    url: urlFor(locale, ''),
    name: BRAND[locale],
    inLanguage: locale,
    publisher: { '@id': orgId(locale) },
  };
}

function aboutPage(locale: Locale): Record<string, unknown> {
  const page = metaFor(locale).pages.about;
  return {
    '@type': 'AboutPage',
    '@id': `${urlFor(locale, 'about')}#webpage`,
    url: urlFor(locale, 'about'),
    name: page.title,
    description: page.description,
    inLanguage: locale,
    isPartOf: { '@id': siteId(locale) },
    about: { '@id': orgId(locale) },
  };
}

function graph(nodes: Record<string, unknown>[]): JsonLdGraph {
  return { '@context': 'https://schema.org', '@graph': nodes };
}

export function structuredDataFor(locale: Locale, stem: RouteStem): JsonLdGraph | null {
  if (stem === '') return graph([organization(locale), website(locale)]);
  if (stem === 'about') return graph([organization(locale), aboutPage(locale)]);
  return null;
}

/* The body of a <script type="application/ld+json">, or null when the route
 * carries no graph.
 *
 * `<` is escaped because vite-prerender-plugin writes a head element's children
 * RAW (serializeElement encodes attributes but returns text nodes untouched),
 * so a literal </script> anywhere in the copy would close the block early. No
 * string contains one today; the escape keeps a future copy edit from turning
 * into an injection. < is a legal JSON escape, so the output still parses. */
export function structuredDataJson(locale: Locale, stem: RouteStem): string | null {
  const data = structuredDataFor(locale, stem);
  return data === null ? null : JSON.stringify(data).replace(/</g, '\\u003c');
}
